import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import openaiImage from '../assets/openai.png';
import geminiImage from '../assets/gemini.png';
import aihubImage from '../assets/aihub.png';
import faqlistImage from '../assets/faqlist.png';
import analyzeImage from '../assets/analyze.png';
import DownloadCTA from '../components/DownloadCTA';

const cardStyle = {
  background: 'radial-gradient(circle at right top, #1A694E -190%, #000000 50%, #000000 100%)',
  borderColor: '#1F3A3A',
};

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Vuzo | Innovating AI Productivity</title>
        <meta name="description" content="Learn how Vuzo is redefining productivity through clean, fast, and intelligent AI tools. Discover our mission to make AI accessible for everyone." />
      </Helmet>

      {/* Hero */}
      <div
        className="relative z-10 w-full py-16 lg:py-24"
        style={{ background: 'radial-gradient(circle at center 150%, #156348 0%, #000000 50%, #000000 100%)' }}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:mt-[50px]">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-14 items-center">
            <div className="space-y-4 w-full lg:w-[60%] text-center lg:text-left">
              <h1 className="text-[48px] lg:text-[74px] font-bold leading-tight text-white">
                <span className="text-[#28F5AB]">Vuzo:</span> The
                <br />Ultimate AI Toolkit
              </h1>
              <p className="text-[18px] leading-relaxed lg:max-w-2xl text-gray-300">
                Start your AI journey with Vuzo. State-of-the-art tools, expert insights, and tailored solutions to help you succeed in an AI-powered world.
              </p>
            </div>
            <div className="flex flex-col items-center lg:items-end">
              <a
                href="https://chat.vuzo.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-[16px] font-semibold transition-all duration-200 shadow-lg bg-[#28F5AB] text-[#0A0A0A] hover:bg-[#1de098] shadow-[0_4px_24px_rgba(40,245,171,0.25)]"
              >
                Start for Free
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Feature cards */}
      <div className="w-full bg-[#0E1314]">
        <div className="max-w-[1280px] mx-auto lg:px-20 py-20">
          <div className="px-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 lg:gap-8">

              <div className="rounded-[16px] p-6 lg:p-8 space-y-4 border md:col-span-2" style={cardStyle}>
                <h3 className="text-[20px] font-bold text-white">Advanced AI Conversation Models</h3>
                <p className="text-[15px] leading-relaxed text-gray-300">
                  Access cutting-edge conversational AI that understands context, maintains coherent dialogue, and provides intelligent responses. Our platform features efficient models for everyday tasks and powerful models for complex reasoning.
                </p>
                <div className="flex items-center justify-center space-x-4 py-8">
                  <img src={openaiImage} alt="OpenAI" className="w-[100px] object-contain" />
                  <img src={geminiImage} alt="Gemini" className="w-[100px] object-contain" />
                </div>
              </div>

              <div className="rounded-[16px] p-6 lg:p-8 space-y-4 border md:col-span-3" style={cardStyle}>
                <h3 className="text-[20px] font-bold text-white">Comprehensive Content Processing Suite</h3>
                <p className="text-[15px] leading-relaxed text-gray-300">
                  Transform any type of content into actionable insights with our intelligent summarization capabilities.
                </p>
                <img src={analyzeImage} alt="analyze" className="w-full object-contain mt-3" />
              </div>

              <div className="rounded-[16px] p-6 lg:p-8 space-y-4 border md:col-span-2" style={cardStyle}>
                <h3 className="text-[20px] font-bold text-white">Continuous Customer Support</h3>
                <p className="text-[15px] leading-relaxed text-gray-300">
                  Our commitment to your success extends beyond our AI tools.
                </p>
                <img src={faqlistImage} alt="faq" className="w-full object-contain mt-3" />
              </div>

              <div className="rounded-[16px] p-6 lg:p-8 space-y-4 border md:col-span-3" style={cardStyle}>
                <h3 className="text-[20px] font-bold text-white">All-in-One AI Hub</h3>
                <p className="text-[15px] leading-relaxed text-gray-300">
                  Our integrated platform brings together multiple AI capabilities in one seamless environment, eliminating the need to switch between different tools and services.
                </p>
                <img src={aihubImage} alt="aihub" className="w-full object-contain mt-3" />
              </div>

            </div>
          </div>
        </div>
      </div>

      <div id="download" className="w-full bg-[#0E1314]">
        <div className="max-w-[1280px] mx-auto">
          <DownloadCTA />
        </div>
      </div>
    </>
  );
};

export default About;
